/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import type {AgentFAQ} from '../../../types/agent';

type AgentFAQsProps = {
  faqs: AgentFAQ[];
  setFaqs: (faqs: AgentFAQ[]) => void;
};

export const AgentFAQs: React.FC<AgentFAQsProps> = ({faqs, setFaqs}) => {
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [isNewFaq, setIsNewFaq] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<AgentFAQ>({questionId: '', question: '', answer: ''});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<AgentFAQ | null>(null);

  const handleNewFaq = () => {
    // Immediately generate a unique id for the new FAQ
    const newFaq = {questionId: uuidv4(), question: '', answer: ''};
    setSelectedFaq(newFaq);
    setIsNewFaq(true);
    setFaqModalOpen(true);
  };

  const handleFaqClick = (faq: AgentFAQ) => {
    setSelectedFaq(faq);
    setIsNewFaq(false);
    setFaqModalOpen(true);
  };

  const handleFaqDelete = (faq: AgentFAQ, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from triggering
    setFaqToDelete(faq);
    setDeleteModalOpen(true);
  };

  const confirmFaqDelete = () => {
    if (!faqToDelete) return;
    setFaqs(faqs.filter((f) => f.questionId !== faqToDelete.questionId));
    setDeleteModalOpen(false);
    setFaqToDelete(null);
  };

  const handleFaqSave = () => {
    const trimmedQuestion = selectedFaq.question.trim();
    const trimmedAnswer = selectedFaq.answer.trim();

    if (!trimmedQuestion || !trimmedAnswer) {
      return;
    }

    if (isNewFaq) {
      setFaqs([...faqs, {questionId: selectedFaq.questionId, question: trimmedQuestion, answer: trimmedAnswer}]);
    } else {
      setFaqs(
        faqs.map((f) =>
          f.questionId === selectedFaq.questionId ? {...f, question: trimmedQuestion, answer: trimmedAnswer} : f,
        ),
      );
    }
    setFaqModalOpen(false);
  };

  return (
    <Stack spacing={2}>
      <Box textAlign={'right'}>
        <Button variant={'contained'} color={'primary'} onClick={handleNewFaq} startIcon={<AddIcon />}>
          New FAQ
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {faqs.length > 0 ? (
              faqs.map((faq) => (
                <TableRow key={faq.questionId} hover style={{cursor: 'pointer'}} onClick={() => handleFaqClick(faq)}>
                  <TableCell>{faq.question}</TableCell>
                  <TableCell align={'right'}>
                    <IconButton color={'error'} onClick={(e) => handleFaqDelete(faq, e)} size={'small'}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align={'center'}>
                  <Stack spacing={1} alignItems={'center'}>
                    <Button variant={'text'} color={'primary'} onClick={handleNewFaq} startIcon={<AddIcon />}>
                      Add an FAQ
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={faqModalOpen} onClose={() => setFaqModalOpen(false)} fullWidth maxWidth={'sm'}>
        <DialogTitle>{isNewFaq ? 'New FAQ' : 'Edit FAQ'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{pt: 2}}>
            <TextField
              label={'Question'}
              fullWidth
              value={selectedFaq.question}
              onChange={(e) => setSelectedFaq({...selectedFaq, question: e.target.value})}
            />
            <TextField
              label={'Answer'}
              fullWidth
              multiline
              rows={4}
              value={selectedFaq.answer}
              onChange={(e) => setSelectedFaq({...selectedFaq, answer: e.target.value})}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaqModalOpen(false)}>Cancel</Button>
          <Button
            variant={'contained'}
            color={'primary'}
            startIcon={<AddIcon />}
            onClick={handleFaqSave}
            disabled={!selectedFaq.question.trim() || !selectedFaq.answer.trim()}
          >
            {isNewFaq ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Are you sure you wish to delete this FAQ?</DialogTitle>
        <DialogContent>
          <Typography>"{faqToDelete?.question}"</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button variant={'contained'} color={'error'} onClick={confirmFaqDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
